<template>
  <!-- Mode téléchargement -->
  <div v-if="creating" data-component="Downloading PDF" class="downloading-container">
    <div class="close-download">
      <CloseButton :onClose="abortDownload" />
    </div>
    <div class="content">
      <Logo v-if="!readyToDownload" />
      <h1>Téléchargement {{ readyToDownload ? 'prêt' : 'en cours' }}</h1>
      <h2><span>{{ totalIds }}</span> visites</h2>
      <VButton v-if="readyToDownload" @click="fetchZip" class="download-button" :disabled="downloading">
        <i :class="['ico', downloading ? 'loading' : 'export']" />
        Télécharger
      </VButton>
      <div class="selected-filters">
        <div 
          v-for="[label, value] in Object.entries(selectedFiltersLabels)" 
          :key="label" 
          class="filter-item"
        >
          {{ label }}
          <div>{{ value }}</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Mode formulaire -->
  <div v-else>
    <form data-component="Export" @submit.prevent class="export-form">
      <div class="form-section">
        <div class="flex w-3/5 mx-auto gap-x-8">
          <div class="flex-1">
            <h4>Gestion du lot</h4>
            <div class="checkboxes">
              <div class="checkbox-field">
                <Checkbox
                  id="has-planification"
                  :modelValue="filters.hasVisitDate"
                  @update:modelValue="toggleVisitDate"
                />
                <label for="has-planification">Date de visite</label>
              </div>
              <div :class="`no-bordered ${isVisitDateOpen ? '' : 'hidden'}`">
                <div :class="`range-date ${isVisitDateOpen ? '' : 'hidden'}`">
                  <div class="flex items-center mb-4">
                    <label class="labeled">Du :</label>
                    <DatePickerInput
                      id="visited-from"
                      :value="filters.visitedFrom"
                      @change="changeStartVisitDate"
                      @reset="resetStartVisitDate"
                    />
                  </div>
                  <div class="flex items-center mb-4">
                    <label class="labeled">Au :</label>
                    <DatePickerInput
                      id="visited-to"
                      :value="filters.visitedTo"
                      @change="changeEndVisitDate"
                      @reset="resetEndVisitDate"
                    />
                  </div>
                </div>
              </div>
              <div class="checkbox-field">
                <Checkbox
                  id="has-hors-scaprim"
                  :modelValue="filters.hasHorsScaprim"
                  @update:modelValue="updateFilter('hasHorsScaprim', $event)"
                />
                <label for="has-hors-scaprim">Gestion hors Scaprim</label>
              </div>
            </div>
            <div class="mb-4">
              <label for="select-team" class="block text-sm font-semibold text-darkblue uppercase mb-2">Agence</label>
              <div class="select is-medium">
                <div class="relative">
                  <select 
                    id="select-team" 
                    data-name="team" 
                    class=""
                    :value="filters.team"
                    @change="updateFilter('team', $event.target.value)"
                  >
                    <option value="">Tous</option>
                    <option 
                      v-for="team in teams" 
                      :key="team.value"
                      :value="team.value"
                    >
                      {{ team.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          <div class="border-separator"></div>
          
          <div class="flex-1">
            <h4>Caractéristiques du lot</h4>

            <div class="columns relative mb-4">
              <input 
                class="w-full h-10 pl-10 pr-4" 
                data-name="address" 
                type="text" 
                placeholder="Ville, Code Postal ..." 
                :value="filters.city || ''" 
                @input="updateFilter('city', $event.target.value)"
              />
              <span class="icon-search absolute z-10">
                <i class="ico search text-sm"></i>
              </span>
            </div>

            <div class="flex mx-0 gap-x-8">
              <div class="flex-1 pl-0">
                <div class="mb-4">
                  <label for="select-property-type" class="block text-sm font-semibold text-darkblue uppercase mb-2">Type</label>
                  <div class="select is-medium">
                    <div class="relative">
                      <select 
                        id="select-property-type" 
                        data-name="type" 
                        class=""
                        :value="filters.propertyType"
                        @change="updateFilter('propertyType', $event.target.value)"
                      >
                        <option value="">Tous</option>
                        <option 
                          v-for="select in propertyTypes" 
                          :key="select.value"
                          :value="select.value"
                        >
                          {{ select.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                
                <div class="mb-4">
                  <label for="select-assistance" class="block text-sm font-semibold text-darkblue uppercase mb-2">Mandat cat</label>
                  <div class="select is-medium">
                    <div class="relative">
                      <select 
                        id="select-assistance" 
                        data-name="assistanceMandator"
                        class=""
                        :value="filters.assistance"
                        @change="updateFilter('assistance', $event.target.value)"
                      >
                        <option value="">Tous</option>
                        <option 
                          v-for="select in assistances" 
                          :key="select.value"
                          :value="select.value"
                        >
                          {{ select.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                
                <div class="mb-4">
                  <label for="select-owner" class="block text-sm font-semibold text-darkblue uppercase mb-2">Propriétaire</label>
                  <div class="select is-medium">
                    <div class="relative">
                      <select 
                        id="select-owner" 
                        data-name="legalOwner"
                        class=""
                        :value="filters.owner"
                        @change="updateFilter('owner', $event.target.value)"
                      >
                        <option value="">Tous</option>
                        <option 
                          v-for="owner in owners" 
                          :key="owner.value"
                          :value="owner.value"
                        >
                          {{ owner.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex-1 pr-0">
                <div class="mb-4">
                  <label for="select-status" class="block text-sm font-semibold text-darkblue uppercase mb-2">Etat d'occupation</label>
                  <div class="select is-medium">
                    <div class="relative">
                      <select 
                        id="select-status" 
                        data-name="occupancy"
                        class=""
                        :value="filters.status"
                        @change="updateFilter('status', $event.target.value)"
                      >
                        <option value="">Tous</option>
                        <option value="vacant">Vacant</option>
                        <option value="occupé">Occupé</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                <div class="mb-4">
                  <label for="select-group-owner" class="block text-sm font-semibold text-darkblue uppercase mb-2">Mandat gpr</label>
                  <div class="select is-medium">
                    <div class="relative">
                      <select 
                        id="select-group-owner" 
                        data-name="managerMandator"
                        class=""
                        :value="filters.groupOwner"
                        @change="updateFilter('groupOwner', $event.target.value)"
                      >
                        <option value="">Tous</option>
                        <option 
                          v-for="select in groupOwners" 
                          :key="select.value"
                          :value="select.value"
                        >
                          {{ select.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                
                <div class="flex mx-0 gap-x-4">
                  <div class="flex-1">
                    <div class="mb-4">
                      <label for="select-unit" class="block text-sm font-semibold text-darkblue uppercase mb-2">Unité</label>
                      <div class="select is-medium">
                        <div class="relative">
                          <select 
                            id="select-unit" 
                            data-name="businessUnit"
                            class=""
                            :value="filters.unit"
                            @change="updateFilter('unit', $event.target.value)"
                          >
                            <option value="">Tous</option>
                            <option 
                              v-for="select in units" 
                              :key="select.value"
                              :value="select.value"
                            >
                              {{ select.label }}
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="flex-1">
                    <div class="mb-4">
                      <label for="select-sub-unit" class="block text-sm font-semibold text-darkblue uppercase mb-2">Sous Unité</label>
                      <div class="select is-medium">
                        <div class="relative">
                          <select 
                            id="select-sub-unit" 
                            data-name="businessSubUnit"
                            class=""
                            :value="filters.subUnit"
                            @change="updateFilter('subUnit', $event.target.value)"
                          >
                            <option value="">Tous</option>
                            <option 
                              v-for="select in subUnits" 
                              :key="select.value"
                              :value="select.value"
                            >
                              {{ select.label }}
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="form-section">
        <div class="flex w-3/5 mx-auto mt-6 pt-4 border-t-2 border-[#E1E9EE] gap-x-8">
          <div class="flex-1">
            <div class="flex mx-0 gap-x-8">
              <div class="flex-1">
                <div class="mb-4">
                  <label for="select-intervention-point" class="block text-sm font-semibold text-darkblue uppercase mb-2">Points de contrôle</label>
                  <div class="select is-medium">
                    <div class="relative">
                      <select 
                        id="select-intervention-point" 
                        @change="selectInterventionPointType" 
                        data-name="interventionPoint"
                        class=""
                        :value="filters.interventionPoint"
                      >
                        <option value="">Tous</option>
                        <option 
                          v-for="select in interventionPoints" 
                          :key="select.value"
                          :value="select.value"
                          :data-type="select.type"
                        >
                          {{ select.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex-1">
                <div class="mb-4">
                  <label for="select-control-answer" class="block text-sm font-semibold text-darkblue uppercase mb-2">Observation</label>
                  <div class="select is-medium">
                    <div class="relative">
                      <select 
                        id="select-control-answer" 
                        data-name="controlAnswer"
                        class=""
                        :value="filters.controlAnswer"
                        @change="updateFilter('controlAnswer', $event.target.value)"
                      >
                        <option value="">Tous</option>
                        <option 
                          v-for="select in controlAnswers" 
                          :key="select.value"
                          :value="select.value"
                        >
                          {{ select.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="border-separator"></div>
          
          <div class="flex-1">
            <div class="mb-4">
              <label for="select-rentable" class="block text-sm font-semibold text-darkblue uppercase mb-2">Le logement est relouable...</label>
              <div class="select is-medium">
                <div class="relative">
                  <select 
                    id="select-rentable" 
                    data-name="rentable"
                    class=""
                    :value="filters.rentable"
                    @change="updateFilter('rentable', $event.target.value)"
                  >
                    <option value="">Tous</option>
                    <option 
                      v-for="select in rentableAnswers" 
                      :key="select.value"
                      :value="select.value"
                    >
                      {{ select.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          <div class="border-separator"></div>
          
          <div class="flex-1">
            <div class="mb-4">
              <label for="select-work-to-be-planned" class="block text-sm font-semibold text-darkblue uppercase mb-2">Y a-t-il du désordre/des travaux à prévoir ?</label>
              <div class="select is-medium">
                <div class="relative">
                  <select 
                    id="select-work-to-be-planned" 
                    data-name="workToBePlanned"
                    class=""
                    :value="filters.workToBePlanned"
                    @change="updateFilter('workToBePlanned', $event.target.value)"
                  >
                    <option value="">Tous</option>
                    <option 
                      v-for="select in workToBePlannedAnswers" 
                      :key="select.value"
                      :value="select.value"
                    >
                      {{ select.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="form-section has-margin-top">
        <div class="export-photos-wrapper">
          <div class="checkboxes">
            <div class="checkbox-field">
              <Checkbox
                id="with-appendix"
                :modelValue="filters.withAppendix"
                @update:modelValue="updateFilter('withAppendix', $event)"
              />
              <label for="with-appendix">Export avec les photos</label>
            </div>
          </div>
        </div>
      </div>
      
      <div class="form-section error">
        <strong v-if="totalIds === 0"><br />Aucune visite trouvée</strong>
      </div>
      
      <div class="form-section submit-section">
        <VButton class="px-8 py-4 text-lg" style="min-width: 200px;" type="button" :disabled="fetchingIds" @click="handleSubmit">
          <i v-if="fetchingIds" class="ico loading" />
          Valider
        </VButton>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { VButton, CloseButton, Checkbox, DatePickerInput } from '../../components/Widget'
import Logo from '../../components/Logo.vue'
import dexieService from '../../services/dexieService'
import api from '../../services/api'
import { QUALITY_POINTS, YESNO_POINTS, RENTABLE_POINTS } from '../../utils/constants'

export default {
  name: 'ExportVisitesView',
  components: {
    VButton,
    CloseButton,
    Checkbox,
    DatePickerInput,
    Logo
  },
  setup() {
    // État réactif
    const creating = ref(false)
    const readyToDownload = ref(false)
    const downloading = ref(false)
    const fetchingIds = ref(false)
    const isVisitDateOpen = ref(false)
    const ids = ref([])
    const totalIds = ref(null)
    const generatedFolderName = ref(null)
    const generationProgress = ref(0)
    
    // Filtres
    const filters = reactive({
      hasVisitDate: false,
      visitedFrom: '',
      visitedTo: '',
      hasHorsScaprim: false,
      team: '',
      city: '',
      propertyType: '',
      assistance: '',
      owner: '',
      status: '',
      groupOwner: '',
      unit: '',
      subUnit: '',
      interventionPoint: '',
      controlAnswer: '',
      rentable: '',
      workToBePlanned: '',
      withAppendix: false
    })
    
    const selectedFiltersLabels = ref({})
    
    // Données de référence (chargées depuis IndexedDB)
    const teams = ref([])
    const propertyTypes = ref([])
    const assistances = ref([])
    const owners = ref([])
    const groupOwners = ref([])
    const units = ref([])
    const subUnits = ref([])
    const interventionPoints = ref([])
    
    // Utiliser les constantes pour les réponses
    const controlAnswers = ref([
      ...QUALITY_POINTS.filter(p => p.value !== 'U'),
      { label: 'Sans objet', value: 'U' },
      ...YESNO_POINTS.filter(p => p.value !== 'U')
    ])
    
    const rentableAnswers = ref([...RENTABLE_POINTS])
    const workToBePlannedAnswers = ref([...YESNO_POINTS])
    
    // Méthodes
    const toggleVisitDate = () => {
      filters.hasVisitDate = !filters.hasVisitDate
      isVisitDateOpen.value = filters.hasVisitDate
    }
    
    const changeStartVisitDate = (value) => {
      filters.visitedFrom = value
    }
    
    const changeEndVisitDate = (value) => {
      filters.visitedTo = value
    }
    
    const resetStartVisitDate = () => {
      filters.visitedFrom = ''
    }
    
    const resetEndVisitDate = () => {
      filters.visitedTo = ''
    }
    
    const updateFilter = (key, value) => {
      filters[key] = value
    }
    
    const selectInterventionPointType = (event) => {
      const value = event.target.value
      filters.interventionPoint = value
    }
    
    const handleSubmit = async (event) => {
      event.preventDefault()
      event.stopPropagation()
      
      if (fetchingIds.value) {
        return
      }
      
      fetchingIds.value = true
      
      try {
        selectedFiltersLabels.value = await buildSelectedFiltersLabels()
        
        creating.value = true
        readyToDownload.value = false
        generationProgress.value = 0
        
        const apiFilters = {}
        
        if (filters.hasVisitDate) {
          if (filters.visitedFrom) {
            apiFilters.visitedFrom = filters.visitedFrom
          }
          if (filters.visitedTo) {
            apiFilters.visitedTo = filters.visitedTo
          }
        }
        
        if (filters.team) apiFilters.team = filters.team
        if (filters.city) apiFilters.city = filters.city
        if (filters.propertyType) apiFilters.propertyType = filters.propertyType
        if (filters.assistance) apiFilters.assistance = filters.assistance
        if (filters.owner) apiFilters.owner = filters.owner
        if (filters.status) apiFilters.status = filters.status
        if (filters.groupOwner) apiFilters.groupOwner = filters.groupOwner
        if (filters.unit) apiFilters.unit = filters.unit
        if (filters.subUnit) apiFilters.subUnit = filters.subUnit
        if (filters.interventionPoint) apiFilters.interventionPoint = filters.interventionPoint
        if (filters.controlAnswer) apiFilters.controlAnswer = filters.controlAnswer
        if (filters.rentable) apiFilters.rentable = filters.rentable
        if (filters.workToBePlanned) apiFilters.workToBePlanned = filters.workToBePlanned
        if (filters.hasHorsScaprim) apiFilters.hasHorsScaprim = filters.hasHorsScaprim
        
        try {
          const response = await api.request('/pdf/visits/generate-zip', {
            method: 'POST',
            body: JSON.stringify({
              filters: apiFilters,
              withAppendix: filters.withAppendix
            })
          })
          
          if (response.success && response.folderName) {
            generatedFolderName.value = response.folderName
            totalIds.value = response.totalRequested
            
            if (response.errors && response.errors.length > 0) {
              console.warn('Erreurs lors de la génération:', response.errors)
            }
            
            readyToDownload.value = true
          } else {
            throw new Error('Échec de la génération du ZIP')
          }
        } catch (apiError) {
          console.error('Erreur lors de la génération du ZIP:', apiError)
          
          if (apiError.message && apiError.message.includes('No visits found')) {
            alert('Aucune visite ne correspond aux filtres sélectionnés.')
          } else {
            alert('Erreur lors de la génération du fichier ZIP. Veuillez réessayer.')
          }
          
          creating.value = false
          readyToDownload.value = false
        }
        
      } catch (error) {
        console.error('Erreur lors de la soumission:', error)
      } finally {
        fetchingIds.value = false
      }
    }
    
    const fetchZip = async () => {
      downloading.value = true
      
      try {
        if (!generatedFolderName.value) {
          throw new Error('Nom du fichier ZIP non disponible')
        }
        
        const downloadUrl = `${api.baseURL}/pdf/visits/download-zip/${generatedFolderName.value}`
        
        const authService = (await import('../../services/authService')).default
        
        const response = await authService.makeAuthenticatedRequest(downloadUrl)
        
        if (!response.ok) {
          throw new Error(`Erreur lors du téléchargement: ${response.status}`)
        }
        
        const blob = await response.blob()
        
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `export-visites-${new Date().toISOString().split('T')[0]}.zip`
        document.body.appendChild(link)
        link.click()
        
        window.URL.revokeObjectURL(url)
        document.body.removeChild(link)
        
        creating.value = false
        readyToDownload.value = false
        downloading.value = false
        generatedFolderName.value = null
        
      } catch (error) {
        console.error('Erreur lors du téléchargement:', error)
        alert('Erreur lors du téléchargement du fichier ZIP. Veuillez réessayer.')
        downloading.value = false
      }
    }
    
    const buildSelectedFiltersLabels = async () => {
      const labels = {}
      
      if (filters.team) {
        const team = teams.value.find(t => t.value === filters.team)
        if (team) labels['Agence'] = team.label
      }
      
      if (filters.city) {
        labels['Ville/CP'] = filters.city
      }
      
      if (filters.propertyType) {
        const type = propertyTypes.value.find(t => t.value === filters.propertyType)
        if (type) labels['Type'] = type.label
      }
      
      if (filters.assistance) {
        const assistance = assistances.value.find(a => a.value === filters.assistance)
        if (assistance) labels['Mandat cat'] = assistance.label
      }
      
      if (filters.owner) {
        const owner = owners.value.find(o => o.value === filters.owner)
        if (owner) labels['Propriétaire'] = owner.label
      }
      
      if (filters.status) {
        labels['Occupation'] = filters.status === 'vacant' ? 'Vacant' : 'Occupé'
      }
      
      if (filters.groupOwner) {
        const groupOwner = groupOwners.value.find(g => g.value === filters.groupOwner)
        if (groupOwner) labels['Mandat gpr'] = groupOwner.label
      }
      
      if (filters.unit) {
        const unit = units.value.find(u => u.value === filters.unit)
        if (unit) labels['Unité'] = unit.label
      }
      
      if (filters.subUnit) {
        const subUnit = subUnits.value.find(s => s.value === filters.subUnit)
        if (subUnit) labels['Sous-unité'] = subUnit.label
      }
      
      if (filters.interventionPoint) {
        const point = interventionPoints.value.find(p => p.value === filters.interventionPoint)
        if (point) labels['Point de contrôle'] = point.label
      }
      
      if (filters.controlAnswer) {
        const answer = controlAnswers.value.find(a => a.value === filters.controlAnswer)
        if (answer) labels['Observation'] = answer.label
      }
      
      if (filters.rentable) {
        const rentable = rentableAnswers.value.find(r => r.value === filters.rentable)
        if (rentable) labels['Relouable'] = rentable.label
      }
      
      if (filters.workToBePlanned) {
        const work = workToBePlannedAnswers.value.find(w => w.value === filters.workToBePlanned)
        if (work) labels['Travaux'] = work.label
      }
      
      if (filters.hasVisitDate && (filters.visitedFrom || filters.visitedTo)) {
        const dates = []
        if (filters.visitedFrom) dates.push(`Du ${filters.visitedFrom}`)
        if (filters.visitedTo) dates.push(`Au ${filters.visitedTo}`)
        labels['Date de visite'] = dates.join(' ')
      }
      
      if (filters.withAppendix) {
        labels['Photos'] = 'Oui'
      }
      
      return labels
    }
    
    const abortDownload = () => {
      creating.value = false
      readyToDownload.value = false
      downloading.value = false
      fetchingIds.value = false
    }
    
    const loadDataFromIndexedDB = async () => {
      try {
        const teamsData = await dexieService.getTeams()
        teams.value = teamsData
          .filter(team => team.enabled !== false)
          .map(team => ({
            value: team.id,
            label: team.name
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        
        const ownersData = await dexieService.db.table('legalOwners').toArray()
        owners.value = ownersData
          .map(owner => ({
            value: owner.id,
            label: owner.name
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        
        const groupOwnersData = await dexieService.db.table('managerMandators').toArray()
        groupOwners.value = groupOwnersData
          .map(mandator => ({
            value: mandator.id,
            label: mandator.name
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        
        const assistancesData = await dexieService.db.table('assistanceMandators').toArray()
        assistances.value = assistancesData
          .map(mandator => ({
            value: mandator.id,
            label: mandator.name
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        
        const propertyTypeDict = await dexieService.getDictionary('property_type')
        if (propertyTypeDict && typeof propertyTypeDict === 'object') {
          propertyTypes.value = Object.entries(propertyTypeDict)
            .map(([key, value]) => ({
              value: key,
              label: value
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        }
        
        const unitDict = await dexieService.getDictionary('unit')
        if (unitDict && typeof unitDict === 'object') {
          units.value = Object.entries(unitDict)
            .map(([key, value]) => ({
              value: key,
              label: value
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        }
        
        const subUnitDict = await dexieService.getDictionary('sub_unit')
        if (subUnitDict && typeof subUnitDict === 'object') {
          subUnits.value = Object.entries(subUnitDict)
            .map(([key, value]) => ({
              value: key,
              label: value
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        }
        
        const interventionPointsData = await dexieService.db.table('interventionPoints').toArray()
        interventionPoints.value = interventionPointsData
          .map(point => ({
            value: point.id,
            label: point.name || point.label || `Point ${point.position}`,
            type: point.type || ''
          }))
          .sort((a, b) => {
            if (interventionPointsData.find(p => p.id === a.value)?.position && 
                interventionPointsData.find(p => p.id === b.value)?.position) {
              return interventionPointsData.find(p => p.id === a.value).position - 
                     interventionPointsData.find(p => p.id === b.value).position
            }
            return a.label.localeCompare(b.label)
          })
        
      } catch (error) {
        console.error('Erreur lors du chargement des données depuis IndexedDB:', error)
      }
    }
    
    onMounted(() => {
      loadDataFromIndexedDB()
    })
    
    return {
      creating,
      readyToDownload,
      downloading,
      fetchingIds,
      isVisitDateOpen,
      ids,
      totalIds,
      filters,
      selectedFiltersLabels,
      teams,
      propertyTypes,
      assistances,
      owners,
      groupOwners,
      units,
      subUnits,
      interventionPoints,
      controlAnswers,
      rentableAnswers,
      workToBePlannedAnswers,
      toggleVisitDate,
      changeStartVisitDate,
      changeEndVisitDate,
      resetStartVisitDate,
      resetEndVisitDate,
      updateFilter,
      selectInterventionPointType,
      handleSubmit,
      fetchZip,
      abortDownload
    }
  }
}
</script>

<style lang="scss" scoped>
@import './Export.scss';
</style>
